import { supabase } from '@/integrations/supabase/client';

export type UserRole = 'READER' | 'EDITOR' | 'ADMIN';

export interface UserRoleRecord {
  id: string;
  user_id: string;
  role: UserRole;
  assigned_by: string | null;
  assigned_at: string;
  created_at: string;
  updated_at: string;
}

/**
 * Get user's role from the database
 * @param userId Clerk user ID
 * @returns User role or 'READER' as default
 */
export async function getUserRole(userId: string): Promise<UserRole> {
  try {
    const { data, error } = await supabase.rpc('get_user_role', {
      clerk_user_id: userId,
    });

    if (error) {
      console.error('Error fetching user role:', error);
      return 'READER'; // Default fallback
    }

    // Type assertion to ensure data matches UserRole type
    const role = data as UserRole;

    // Validate that the role is actually one of our allowed values
    if (role && ['READER', 'EDITOR', 'ADMIN'].includes(role)) {
      return role;
    }

    return 'READER';
  } catch (error) {
    console.error('Error in getUserRole:', error);
    return 'READER';
  }
}

/**
 * Set user's role in the database
 * @param userId Clerk user ID
 * @param role New role to assign
 * @param assignedBy User ID of the admin assigning this role
 */
export async function setUserRole(
  userId: string,
  role: UserRole,
  assignedBy?: string
): Promise<boolean> {
  try {
    const { error } = await supabase.rpc('upsert_user_role', {
      clerk_user_id: userId,
      new_role: role,
      assigned_by_user_id: assignedBy || undefined,
    });

    if (error) {
      console.error('Error setting user role:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in setUserRole:', error);
    return false;
  }
}

/**
 * Initialize user role when they first sign up (defaults to READER)
 * Only creates a role if one doesn't already exist - preserves existing roles
 * @param userId Clerk user ID
 */
export async function initializeUserRole(userId: string): Promise<void> {
  try {
    // Use the new initialize_user_role function that preserves existing roles
    const { error } = await supabase.rpc('initialize_user_role', {
      clerk_user_id: userId,
    });

    if (error) {
      console.error('Error initializing user role:', error);
    }
  } catch (error) {
    console.error('Error in initializeUserRole:', error);
  }
}

/**
 * Check if user has permission for a specific action
 */
export function hasPermission(
  userRole: UserRole,
  requiredRole: UserRole
): boolean {
  const roleHierarchy: Record<UserRole, number> = {
    READER: 1,
    EDITOR: 2,
    ADMIN: 3,
  };

  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
}

/**
 * Role-based permission checks
 */
export function canCreateArticle(userRole: UserRole): boolean {
  return hasPermission(userRole, 'EDITOR');
}

export function canEditArticle(userRole: UserRole): boolean {
  return hasPermission(userRole, 'EDITOR');
}

export function canDeleteArticle(userRole: UserRole): boolean {
  return hasPermission(userRole, 'ADMIN');
}

export function canManageRoles(userRole: UserRole): boolean {
  return hasPermission(userRole, 'ADMIN');
}

export function canCreateQuestionCatalog(userRole: UserRole): boolean {
  return hasPermission(userRole, 'EDITOR');
}

export function canEditQuestionCatalog(userRole: UserRole): boolean {
  return hasPermission(userRole, 'EDITOR');
}

export function canDeleteQuestionCatalog(userRole: UserRole): boolean {
  return hasPermission(userRole, 'ADMIN');
}
